import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { User, Lock, ArrowRight, ChevronLeft, Building2, LayoutDashboard, IdCard, Check, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../lib/toast";
import { Button } from "../lib/ui";
import { MASCOTS } from "../lib/constants";
import { useT, LanguageToggle } from "../lib/i18n";
import { BrandIcon, BrandWordmark } from "../components/Brand";
import { useBranding } from "../context/BrandingContext";
import { getTenant, setTenant } from "../lib/tenant";
import api from "../lib/api";

export default function Login() {
  const { login, user, applyToken } = useAuth();
  const { refresh: refreshBranding, companyName } = useBranding();
  const toast = useToast();
  const { t } = useT();
  const navigate = useNavigate();

  const [step, setStep] = useState(() =>
    (typeof window !== "undefined" && window.location.hash.includes("session_id=")) ? "google" : "loading"
  );   // loading | company | portal | erp | google | google-pending
  const [tenants, setTenants] = useState([]);
  const [selected, setSelected] = useState(null);
  const [multi, setMulti] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [pendingInfo, setPendingInfo] = useState(null);

  useEffect(() => { if (user) navigate("/"); }, [user, navigate]);

  const pickTenant = useCallback((tenantObj, goPortal = true) => {
    setSelected(tenantObj);
    setTenant(tenantObj);
    refreshBranding();
    if (goPortal) setStep("portal");
  }, [refreshBranding]);

  // Resolve tenant: domain first (auto-skip company step), else list companies.
  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash.includes("session_id=")) return; // Google callback in progress
    let done = false;
    (async () => {
      try {
        const { data } = await api.get("/public/current-tenant");
        if (done) return;
        if (data.tenant) { pickTenant(data.tenant); return; }
      } catch { /* ignore */ }
      try {
        const { data } = await api.get("/public/tenants");
        if (done) return;
        setTenants(data || []);
        setMulti((data || []).length > 1);
        if ((data || []).length === 1) { pickTenant(data[0]); }
        else if ((data || []).length === 0) { setStep("portal"); }
        else {
          const stored = getTenant();
          const match = stored && data.find((d) => d.slug === stored.slug);
          if (match) { setSelected(match); setStep("portal"); }
          else setStep("company");
        }
      } catch { setStep("portal"); }
    })();
    return () => { done = true; };
  }, [pickTenant]);

  // Google Sign-In callback: user returns to /login#session_id=... → exchange for
  // OUR Central Flow JWT (approved) or show the pending-approval screen.
  useEffect(() => {
    const m = window.location.hash.match(/session_id=([^&]+)/);
    if (!m) return;
    const sid = m[1];
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
    (async () => {
      setGoogleBusy(true);
      try {
        const { data } = await api.post("/auth/google", { session_id: sid });
        if (data.status === "approved" && data.access_token) {
          await applyToken(data.access_token);
          toast.success(t("Welcome back!"));
          navigate("/");
        } else {
          setPendingInfo({ email: data.email, name: data.name, message: data.message });
          setStep("google-pending");
        }
      } catch (err) {
        toast.error(err.response?.data?.detail || "Login Google gagal");
        setStep("erp");
      } finally {
        setGoogleBusy(false);
      }
    })();
    // eslint-disable-next-line
  }, []);

  const startGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/login";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submitErp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(username, password);
      toast.success(t("Welcome back!"));
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("Login failed"));
    } finally { setLoading(false); }
  };

  const brandName = selected?.name || companyName || "Central Flow";

  return (
    <div className="flex min-h-screen">
      {/* Left brand panel */}
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-ink p-12 text-white lg:flex">
        <img src={MASCOTS.loginBg} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="relative flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white p-1.5 shadow-lg">
            <BrandIcon size={36} rounded="rounded-lg" />
          </div>
          <BrandWordmark tone="light" className="text-2xl" />
        </div>
        <div className="relative max-w-md">
          <h1 className="font-heading text-4xl font-extrabold leading-tight">
            {t("One kitchen command center for every branch.")}
          </h1>
          <p className="mt-4 text-stone-300">
            {t("Procurement, purchasing, warehouse, delivery, inventory and waste — centralized and beautifully simple.")}
          </p>
        </div>
        <p className="relative text-sm text-stone-400">{selected ? brandName : "Central Flow · Multi-Restaurant Platform"}</p>
      </div>

      {/* Right content */}
      <div className="flex w-full flex-col justify-center bg-app px-6 py-12 lg:w-1/2 lg:px-20">
        <div className="mx-auto w-full max-w-md">
          <div className="mb-8 flex items-center gap-2.5 lg:hidden">
            <BrandIcon size={42} />
            <BrandWordmark className="text-xl" />
          </div>

          {step === "loading" && (
            <div className="flex h-40 items-center justify-center text-ink-mute" data-testid="login-loading">
              <Loader2 size={26} className="animate-spin" />
            </div>
          )}

          {step === "google" && (
            <div className="flex h-40 flex-col items-center justify-center gap-3 text-ink-mute" data-testid="login-google-processing">
              <Loader2 size={26} className="animate-spin" />
              <p className="text-sm">Memproses login Google…</p>
            </div>
          )}

          {step === "google-pending" && (
            <div data-testid="login-google-pending">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-100 text-amber-600"><IdCard size={24} /></div>
              <h2 className="font-heading text-2xl font-bold text-ink">Menunggu Persetujuan</h2>
              <p className="mt-2 text-sm text-ink-soft">{pendingInfo?.message || "Akun Google Anda menunggu persetujuan admin."}</p>
              {pendingInfo?.email && <p className="mt-1 text-sm font-semibold text-ink">{pendingInfo.email}</p>}
              <p className="mt-4 text-xs text-ink-mute">Admin / Owner akan menetapkan Role &amp; Cabang Anda. Silakan masuk kembali setelah akun disetujui.</p>
              <Button variant="outline" className="mt-6" onClick={() => { setStep("erp"); setPendingInfo(null); }} data-testid="google-pending-back">Kembali</Button>
            </div>
          )}

          {/* STEP 1 — Choose Company */}
          {step === "company" && (
            <div data-testid="login-company-step">
              <h2 className="font-heading text-3xl font-bold text-ink">Pilih Perusahaan</h2>
              <p className="mt-1 text-sm text-ink-soft">Pilih restoran / brand Anda untuk melanjutkan.</p>
              <div className="mt-8 space-y-2.5">
                {tenants.map((tn) => (
                  <button key={tn.slug} type="button" onClick={() => pickTenant(tn)} data-testid={`company-option-${tn.slug}`}
                    className="group flex w-full items-center justify-between rounded-xl border border-line bg-white px-4 py-4 text-left transition-all hover:border-primary hover:shadow-pop">
                    <span className="flex items-center gap-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary"><Building2 size={20} /></span>
                      <span>
                        <span className="block font-heading text-base font-bold text-ink">{tn.brand || tn.name}</span>
                        <span className="block text-xs text-ink-mute">{tn.name}</span>
                      </span>
                    </span>
                    <ArrowRight size={18} className="text-ink-mute transition-transform group-hover:translate-x-1 group-hover:text-primary" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* STEP 2 — Choose Portal */}
          {step === "portal" && (
            <div data-testid="login-portal-step">
              <h2 className="font-heading text-3xl font-bold text-ink">Pilih Portal</h2>
              <p className="mt-1 flex flex-wrap items-center gap-1.5 text-sm text-ink-soft">
                {selected && <><Check size={14} className="text-primary" /> <span className="font-semibold text-ink">{brandName}</span></>}
                {multi && (
                  <button type="button" onClick={() => setStep("company")} data-testid="change-company-btn" className="text-primary underline decoration-dotted underline-offset-2 hover:text-primary/80">Ganti perusahaan</button>
                )}
              </p>
              <div className="mt-8 space-y-3">
                <button type="button" onClick={() => setStep("erp")} data-testid="portal-erp-btn"
                  className="group flex w-full items-center justify-between rounded-xl border border-line bg-white px-5 py-4 text-left transition-all hover:border-primary hover:shadow-pop">
                  <span className="flex items-center gap-3">
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-amber-500 text-white"><LayoutDashboard size={22} /></span>
                    <span>
                      <span className="block font-heading text-base font-bold text-ink">ERP / Operasional</span>
                      <span className="block text-xs text-ink-mute">Dashboard, dapur, inventori, keuangan & POS</span>
                    </span>
                  </span>
                  <ArrowRight size={18} className="text-ink-mute transition-transform group-hover:translate-x-1 group-hover:text-primary" />
                </button>
                <button type="button" onClick={() => navigate("/karyawan/login")} data-testid="portal-attendance-btn"
                  className="group flex w-full items-center justify-between rounded-xl border border-line bg-white px-5 py-4 text-left transition-all hover:border-primary hover:shadow-pop">
                  <span className="flex items-center gap-3">
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 text-white"><IdCard size={22} /></span>
                    <span>
                      <span className="block font-heading text-base font-bold text-ink">Absensi / Portal HRD</span>
                      <span className="block text-xs text-ink-mute">Masuk dengan Employee ID & PIN</span>
                    </span>
                  </span>
                  <ArrowRight size={18} className="text-ink-mute transition-transform group-hover:translate-x-1 group-hover:text-primary" />
                </button>
              </div>
            </div>
          )}

          {/* STEP 3 — ERP credentials */}
          {step === "erp" && (
            <div>
              <button type="button" onClick={() => setStep("portal")} data-testid="back-to-portal-btn" className="mb-3 flex items-center gap-1 text-sm text-ink-soft hover:text-ink">
                <ChevronLeft size={16} /> Kembali
              </button>
              <h2 className="font-heading text-3xl font-bold text-ink">{t("Sign in")}</h2>
              <p className="mt-1 text-sm text-ink-soft">{selected ? <>ERP · <span className="font-semibold text-ink">{brandName}</span></> : t("Access your operations dashboard.")}</p>

              <form onSubmit={submitErp} className="mt-8 space-y-4">
                <div>
                  <label className="cf-label">{t("Username")}</label>
                  <div className="relative">
                    <User size={18} className="absolute left-3.5 top-3 text-ink-mute" />
                    <input type="text" required autoCapitalize="none" autoCorrect="off" value={username}
                      onChange={(e) => setUsername(e.target.value)} placeholder={t("Username")}
                      className="cf-input pl-10" data-testid="login-username-input" />
                  </div>
                </div>
                <div>
                  <label className="cf-label">{t("Password")}</label>
                  <div className="relative">
                    <Lock size={18} className="absolute left-3.5 top-3 text-ink-mute" />
                    <input type="password" required value={password}
                      onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                      className="cf-input pl-10" data-testid="login-password-input" />
                  </div>
                </div>
                <Button type="submit" className="w-full" size="lg" loading={loading} data-testid="login-submit-button">
                  {t("Sign In")}
                </Button>
              </form>

              <div className="mt-6 flex items-center gap-3">
                <span className="h-px flex-1 bg-line" />
                <span className="text-xs text-ink-mute">atau</span>
                <span className="h-px flex-1 bg-line" />
              </div>
              <button type="button" onClick={startGoogle} disabled={googleBusy} data-testid="google-signin-button"
                className="mt-4 flex w-full items-center justify-center gap-3 rounded-xl border border-line bg-white px-4 py-3 text-sm font-semibold text-ink transition-all hover:border-primary hover:shadow-pop disabled:opacity-60">
                <GoogleG />
                {googleBusy ? "Memproses…" : "Masuk dengan Google"}
              </button>
              <p className="mt-2 text-center text-xs text-ink-mute">Akun Google baru memerlukan persetujuan admin sebelum akses ERP.</p>
            </div>
          )}

          <div className="mt-8 flex items-center gap-2">
            <span className="text-xs text-ink-mute">{t("Language")}</span>
            <LanguageToggle />
          </div>
        </div>
      </div>
    </div>
  );
}

function GoogleG() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
      <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
      <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
      <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
    </svg>
  );
}
